import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowsClockwise,
    ArrowRight,
    BatteryHigh,
    House,
    Plus,
    SealCheck,
    Sun,
    X,
} from '@phosphor-icons/react';
import type { CombinedFinancingSummary } from './financingCombinedSummary';
import { partnerLabelForCombined } from './financingCombinedSummary';
import {
    buildFinancingAiCombinedProducts,
    defaultSelectedPlanId,
    formatFinancingAiProductPrice,
    savingsForMonthlyPayment,
    selectedPlanCoveredProducts,
    selectedPlanDisplayMonthly,
    selectedPlanPriceIsCash,
    splitAddonMonthlyByBillOffset,
    type FinancingAiAddon,
    type FinancingAiPlan,
    type FinancingAiPlanMetric,
} from './financingAiPlansData';
import {
    atomicProductTypesFromFinancingProduct,
    type GenerateQuoteSyncScope,
} from './generateQuoteSyncGate';

/** Which combined pricing lines the user kept included when triggering a quote. */
export type FinancingAiQuoteContext = {
    includedLineIds: string[];
    /** Atomic products the displayed price covers (Solar / Roof / Battery). */
    coveredProducts: string[];
    /**
     * `combined` = selected-plan CTA (all included lines).
     * `plan` = alternative card CTA (that card's partner + product only).
     */
    quoteScope: 'combined' | 'plan';
};

export type FinancingAiPlansViewProps = {
    plans: FinancingAiPlan[];
    combinedSummary: CombinedFinancingSummary;
    loading?: boolean;
    onRefreshAi?: () => void;
    onSelectPlan?: (plan: FinancingAiPlan) => void;
    onGenerateQuote?: (plan: FinancingAiPlan, context: FinancingAiQuoteContext) => void;
    /** Permission / context block only — not product design misses. */
    generateQuoteDisabled?: boolean;
    generateQuoteDisabledReason?: string;
    getQuoteDesignBlockedReasonForPlan?: (plan: FinancingAiPlan) => string | undefined;
    onQuoteDesignBlocked?: (reason: string) => void;
    getQuoteSyncBlockedHint?: (plan: FinancingAiPlan) => string | undefined;
    quoteSyncBlockedHintForScope?: (scope?: GenerateQuoteSyncScope) => string | undefined;
    /** Pricing-grid cells the user actually selected — used to restore a plan, not to default LightReach. */
    selectedCellKeys?: Iterable<string> | null;
};

function formatMoney(value: number, decimals = 2): string {
    return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;
}

const AI_SCORE_RING_SIZE = 52;
const AI_SCORE_RING_STROKE = 3.5;
const AI_SCORE_RING_CENTER = AI_SCORE_RING_SIZE / 2;
const AI_SCORE_RING_RADIUS = (AI_SCORE_RING_SIZE - AI_SCORE_RING_STROKE) / 2;
const AI_SCORE_RING_CIRCUMFERENCE = 2 * Math.PI * AI_SCORE_RING_RADIUS;

function AiScoreRing({ score }: { score: number | null }) {
    const hasScore = score != null && Number.isFinite(score);
    const safeScore = hasScore ? Math.max(0, Math.min(100, Math.round(score))) : 0;
    const dash = hasScore ? AI_SCORE_RING_CIRCUMFERENCE * (safeScore / 100) : 0;
    const label = hasScore ? `AI score ${safeScore} out of 100` : 'No AI score until a plan is selected';

    return (
        <div className="obd-financing-ai__score-ring" title={label} aria-label={label}>
            <svg
                className="obd-financing-ai__score-ring-svg"
                width={AI_SCORE_RING_SIZE}
                height={AI_SCORE_RING_SIZE}
                viewBox={`0 0 ${AI_SCORE_RING_SIZE} ${AI_SCORE_RING_SIZE}`}
                aria-hidden
            >
                <circle
                    className="obd-financing-ai__score-ring-track"
                    cx={AI_SCORE_RING_CENTER}
                    cy={AI_SCORE_RING_CENTER}
                    r={AI_SCORE_RING_RADIUS}
                    fill="none"
                    strokeWidth={AI_SCORE_RING_STROKE}
                />
                <circle
                    className="obd-financing-ai__score-ring-progress"
                    cx={AI_SCORE_RING_CENTER}
                    cy={AI_SCORE_RING_CENTER}
                    r={AI_SCORE_RING_RADIUS}
                    fill="none"
                    strokeWidth={AI_SCORE_RING_STROKE}
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${AI_SCORE_RING_CIRCUMFERENCE}`}
                    transform={`rotate(-90 ${AI_SCORE_RING_CENTER} ${AI_SCORE_RING_CENTER})`}
                />
            </svg>
            <span className="obd-financing-ai__score-ring-value">{hasScore ? safeScore : '—'}</span>
        </div>
    );
}

function altCardBodyMetrics(plan: FinancingAiPlan): FinancingAiPlanMetric[] {
    return plan.metrics.filter((metric) => {
        const label = metric.label.trim().toLowerCase();
        return label !== 'cash price' && label !== 'per month' && label !== 'monthly';
    });
}

function altCardHeroAmount(plan: FinancingAiPlan): string {
    if (plan.isCash) {
        return formatMoney(plan.cashPrice ?? plan.monthly, 0);
    }
    return formatMoney(plan.monthly);
}

function addonProductIcon(product: FinancingAiAddon) {
    const haystack = `${product.productTags.join(' ')} ${product.subtitle} ${product.title}`.toLowerCase();
    if (haystack.includes('battery')) return <BatteryHigh size={16} weight="duotone" />;
    if (haystack.includes('solar')) return <Sun size={16} weight="duotone" />;
    return <House size={16} weight="duotone" />;
}

type SelectedPartner = { partner: string; label: string };

function joinWithMiddleDot(items: string[]): string {
    return items.filter(Boolean).join(' · ');
}

function SelectedPlanHero({
    hasSelection,
    partners,
    products,
    insightTags,
    score,
    priceIsCash,
    displayMonthly,
    selectedPlan,
    generateQuoteDisabled,
    generateQuoteDisabledReason,
    quoteDesignBlockedReason,
    onQuoteDesignBlocked,
    getQuoteSyncBlockedHint,
    quoteSyncBlockedHintForScope,
    combinedQuoteSyncScope,
    onGenerateQuote,
}: {
    hasSelection: boolean;
    partners: SelectedPartner[];
    products: string[];
    insightTags: string[];
    score: number | null | undefined;
    priceIsCash: boolean;
    displayMonthly: number;
    selectedPlan: FinancingAiPlan | null;
    generateQuoteDisabled?: boolean;
    generateQuoteDisabledReason?: string;
    quoteDesignBlockedReason?: string;
    onQuoteDesignBlocked?: (reason: string) => void;
    getQuoteSyncBlockedHint?: (plan: FinancingAiPlan) => string | undefined;
    quoteSyncBlockedHintForScope?: (scope?: GenerateQuoteSyncScope) => string | undefined;
    combinedQuoteSyncScope?: GenerateQuoteSyncScope;
    onGenerateQuote: () => void;
}) {
    const hasPrice = hasSelection || displayMonthly > 0;
    const priceAmount =
        priceIsCash && selectedPlan
            ? formatMoney(selectedPlan.cashPrice ?? selectedPlan.monthly, 0)
            : formatMoney(displayMonthly);

    const partnerLabels = partners.map((item) => item.label).filter(Boolean);
    const productLabels =
        products.length > 0
            ? products
            : selectedPlan
              ? String(selectedPlan.product)
                    .split('+')
                    .map((part) => part.trim())
                    .filter(Boolean)
              : [];

    return (
        <div className="obd-financing-ai__hero">
            <div className="obd-financing-ai__hero-lead">
                <AiScoreRing score={hasSelection ? (score ?? null) : null} />
                <div className="obd-financing-ai__hero-identity">
                    {!hasSelection || partnerLabels.length === 0 ? (
                        <>
                            <p className="obd-financing-ai__hero-headline obd-financing-ai__hero-headline--empty">
                                No plan selected
                            </p>
                            <p className="obd-financing-ai__hero-detail obd-financing-ai__hero-detail--empty-hint">
                                Choose a ranked option below
                            </p>
                        </>
                    ) : (
                        <>
                            <p
                                className="obd-financing-ai__hero-headline"
                                aria-label={`Finance partners: ${partnerLabels.join(', ')}`}
                            >
                                {joinWithMiddleDot(partnerLabels)}
                            </p>
                            <div className="obd-financing-ai__hero-detail">
                                {productLabels.length > 0 ? (
                                    <p className="obd-financing-ai__hero-meta-row">
                                        <span className="obd-financing-ai__hero-detail-label">Products</span>
                                        <span
                                            className="obd-financing-ai__hero-meta-value"
                                            aria-label={`Products covered: ${productLabels.join(', ')}`}
                                        >
                                            {joinWithMiddleDot(productLabels)}
                                        </span>
                                    </p>
                                ) : null}
                                {insightTags.length > 0 ? (
                                    <div className="obd-financing-ai__hero-tags">
                                        {insightTags.slice(0, 2).map((tag) => (
                                            <span key={tag} className="obd-financing-ai__hero-tag">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="obd-financing-ai__hero-aside">
                <div className="obd-financing-ai__hero-price">
                    <span className="obd-financing-ai__hero-price-label">
                        {priceIsCash ? 'Cash price' : 'Per month'}
                    </span>
                    <span className="obd-financing-ai__hero-price-value">
                        {hasPrice ? priceAmount : '—'}
                        {!priceIsCash && hasPrice ? <small>/mo</small> : null}
                    </span>
                </div>
                <button
                    type="button"
                    className={`obd-financing-ai__hero-cta${quoteDesignBlockedReason ? ' obd-financing-ai__hero-cta--quote-disabled' : ''}`}
                    disabled={!hasSelection || generateQuoteDisabled}
                    title={
                        !hasSelection
                            ? 'Select a plan to generate a quote'
                            : generateQuoteDisabled
                              ? generateQuoteDisabledReason
                              : quoteDesignBlockedReason ??
                                quoteSyncBlockedHintForScope?.(combinedQuoteSyncScope) ??
                                (selectedPlan && getQuoteSyncBlockedHint?.(selectedPlan)) ??
                                'Open generate quote with this plan'
                    }
                    onClick={() => {
                        if (!hasSelection || generateQuoteDisabled) return;
                        if (quoteDesignBlockedReason) {
                            onQuoteDesignBlocked?.(quoteDesignBlockedReason);
                            return;
                        }
                        onGenerateQuote();
                    }}
                >
                    Generate Quote
                    <ArrowRight size={14} weight="bold" aria-hidden />
                </button>
            </div>
        </div>
    );
}

function MonthlyCostChart({
    plan,
    displayMonthly,
    billAfter,
}: {
    plan: FinancingAiPlan;
    displayMonthly: number;
    /** Residual utility bill under this plan, recomputed from the products currently included. */
    billAfter: number | null;
}) {
    if (plan.billToday == null) {
        return (
            <div className="obd-financing-ai__chart obd-financing-ai__chart--bars">
                <p className="obd-financing-ai__chart-title">Monthly cost comparison</p>
                <p className="obd-financing-ai__chart-empty">
                    Enter All In Rate and Annual Usage in Utility Details to compare current bill vs. plan payment.
                </p>
                <p className="obd-financing-ai__chart-highlight">
                    Combined plan payment: <strong>{formatMoney(displayMonthly)}/mo</strong>
                </p>
            </div>
        );
    }

    const residualBill = billAfter ?? plan.electricAfter ?? displayMonthly;
    const max = Math.max(plan.billToday, residualBill, displayMonthly, 1);
    const bar = (value: number) => `${Math.max(8, (value / max) * 100)}%`;

    return (
        <div className="obd-financing-ai__chart obd-financing-ai__chart--bars">
            <p className="obd-financing-ai__chart-title">Monthly cost comparison</p>
            <div className="obd-financing-ai__bar-rows">
                <div className="obd-financing-ai__bar-row">
                    <span className="obd-financing-ai__bar-label">Current bill</span>
                    <div className="obd-financing-ai__bar-track">
                        <span
                            className="obd-financing-ai__bar obd-financing-ai__bar--danger"
                            style={{ width: bar(plan.billToday) }}
                        />
                    </div>
                    <span className="obd-financing-ai__bar-value">{formatMoney(plan.billToday)}</span>
                </div>
                <div className="obd-financing-ai__bar-row">
                    <span className="obd-financing-ai__bar-label">Bill after solar</span>
                    <div className="obd-financing-ai__bar-track">
                        <span
                            className="obd-financing-ai__bar obd-financing-ai__bar--success"
                            style={{ width: bar(residualBill) }}
                        />
                    </div>
                    <span className="obd-financing-ai__bar-value">{formatMoney(residualBill)}</span>
                </div>
                <div className="obd-financing-ai__bar-row">
                    <span className="obd-financing-ai__bar-label">Combined payment</span>
                    <div className="obd-financing-ai__bar-track">
                        <span
                            className="obd-financing-ai__bar obd-financing-ai__bar--primary"
                            style={{ width: bar(displayMonthly) }}
                        />
                    </div>
                    <span className="obd-financing-ai__bar-value">{formatMoney(displayMonthly)}</span>
                </div>
            </div>
        </div>
    );
}

function CumulativeSpendChart({
    plan,
    displayMonthly,
    priceIsCash,
}: {
    plan: FinancingAiPlan;
    displayMonthly: number;
    priceIsCash: boolean;
}) {
    const withoutSolar =
        plan.billToday != null && plan.billToday > 0 ? plan.billToday * 12 * 25 : null;
    const withThisPlan = priceIsCash ? plan.cashPrice ?? plan.monthly : displayMonthly * 12 * 25;

    if (withoutSolar == null) {
        return (
            <div className="obd-financing-ai__chart obd-financing-ai__chart--area">
                <p className="obd-financing-ai__chart-title">What you pay over 25 years</p>
                <p className="obd-financing-ai__chart-empty">
                    Add All-in rate and annual usage in Utility Details to compare staying on the utility versus this plan.
                </p>
            </div>
        );
    }

    const saved = Math.max(0, withoutSolar - withThisPlan);
    const max = Math.max(withoutSolar, withThisPlan, 1);
    const barWidth = (value: number) => `${Math.max(8, (value / max) * 100)}%`;
    const planLabel = priceIsCash ? 'Cash purchase (one-time)' : 'This plan (25 years of payments)';

    return (
        <div className="obd-financing-ai__chart obd-financing-ai__chart--area">
            <p className="obd-financing-ai__chart-title">What you pay over 25 years</p>
            <p className="obd-financing-ai__chart-subtitle">
                Staying on today’s utility bill versus paying this plan. Uses current rates — no inflation.
            </p>
            <div className="obd-financing-ai__bar-rows">
                <div className="obd-financing-ai__bar-row">
                    <span className="obd-financing-ai__bar-label">Stay with utility</span>
                    <div className="obd-financing-ai__bar-track">
                        <span
                            className="obd-financing-ai__bar obd-financing-ai__bar--danger"
                            style={{ width: barWidth(withoutSolar) }}
                        />
                    </div>
                    <span className="obd-financing-ai__bar-value">{formatMoney(withoutSolar, 0)}</span>
                </div>
                <div className="obd-financing-ai__bar-row">
                    <span className="obd-financing-ai__bar-label">{priceIsCash ? 'Cash price' : 'This plan'}</span>
                    <div className="obd-financing-ai__bar-track">
                        <span
                            className="obd-financing-ai__bar obd-financing-ai__bar--primary"
                            style={{ width: barWidth(withThisPlan) }}
                        />
                    </div>
                    <span className="obd-financing-ai__bar-value">{formatMoney(withThisPlan, 0)}</span>
                </div>
            </div>
            <p className="obd-financing-ai__chart-highlight">
                {saved > 0 ? (
                    <>
                        Estimated you keep <strong>{formatMoney(saved, 0)}</strong> versus staying with the utility.
                    </>
                ) : (
                    <>{planLabel} costs as much or more than staying with the utility at today’s rates.</>
                )}
            </p>
        </div>
    );
}

/**
 * Spells out what the savings figures above it are actually comparing, since roof financing is part
 * of the monthly payment but never part of the electric-bill savings.
 */
function SavingsBasisNote({
    billToday,
    energyMonthly,
    nonEnergyMonthly,
    isCash,
    hasSavings,
}: {
    billToday: number | null;
    energyMonthly: number;
    nonEnergyMonthly: number;
    isCash: boolean;
    /** False when this selection offsets nothing on the electric bill (roof-only, or no bill). */
    hasSavings: boolean;
}) {
    const roofNote =
        nonEnergyMonthly > 0
            ? ` Roof financing (${formatMoney(nonEnergyMonthly)}/mo) is left out — a new roof doesn’t reduce the electric bill.`
            : '';

    let body: React.ReactNode;

    if (billToday == null || billToday <= 0) {
        body = (
            <>
                Savings need a current electric bill. Add <strong>All-in rate</strong> and{' '}
                <strong>annual usage</strong> in Utility Details to compare this payment with the bill.
                {roofNote}
            </>
        );
    } else if (isCash && hasSavings) {
        body = (
            <>
                Cash has no monthly payment, so savings equal the full{' '}
                <strong>{formatMoney(billToday)}/mo</strong> electric bill, held at today’s rates — no
                utility inflation.
            </>
        );
    } else if (!hasSavings || energyMonthly <= 0) {
        body = (
            <>
                Nothing in this selection offsets electric usage, so no electric-bill savings are
                shown.
                {roofNote || ' Roof financing is a home-improvement cost, not a utility offset.'}
            </>
        );
    } else if (energyMonthly >= billToday) {
        body = (
            <>
                The solar &amp; battery payment (<strong>{formatMoney(energyMonthly)}/mo</strong>) is at
                or above the <strong>{formatMoney(billToday)}/mo</strong> electric bill, so there are no
                monthly savings at today’s rates.{roofNote}
            </>
        );
    } else {
        body = (
            <>
                Savings compare the <strong>{formatMoney(billToday)}/mo</strong> electric bill with the{' '}
                <strong>{formatMoney(energyMonthly)}/mo</strong> solar &amp; battery payment.{roofNote}{' '}
                Year-1 is the monthly difference × 12, and 25-yr is Year-1 × 25 at today’s rates — no
                escalator or utility inflation applied.
            </>
        );
    }

    return <p className="obd-financing-ai__savings-note">{body}</p>;
}

export const FinancingAiPlansView: React.FC<FinancingAiPlansViewProps> = ({
    plans,
    combinedSummary,
    loading = false,
    onRefreshAi,
    onSelectPlan,
    onGenerateQuote,
    generateQuoteDisabled = false,
    generateQuoteDisabledReason,
    getQuoteDesignBlockedReasonForPlan,
    onQuoteDesignBlocked,
    getQuoteSyncBlockedHint,
    quoteSyncBlockedHintForScope,
    selectedCellKeys,
}) => {
    const [selectedPlanId, setSelectedPlanId] = useState(() =>
        defaultSelectedPlanId(plans, selectedCellKeys),
    );
    const [includedOverrides, setIncludedOverrides] = useState<Record<string, boolean>>({});

    useEffect(() => {
        setSelectedPlanId((currentId) => {
            if (currentId && plans.some((plan) => plan.id === currentId)) return currentId;
            return defaultSelectedPlanId(plans, selectedCellKeys);
        });
    }, [plans, selectedCellKeys]);

    const selectedPlan = useMemo(
        () => plans.find((p) => p.id === selectedPlanId) ?? null,
        [plans, selectedPlanId],
    );

    const baseCombinedProducts = useMemo(
        () => buildFinancingAiCombinedProducts(combinedSummary, selectedPlan),
        [combinedSummary, selectedPlan],
    );

    useEffect(() => {
        const validIds = new Set(baseCombinedProducts.map((product) => product.id));
        setIncludedOverrides((prev) => {
            const next: Record<string, boolean> = {};
            let changed = false;
            for (const [id, included] of Object.entries(prev)) {
                if (validIds.has(id)) {
                    next[id] = included;
                } else {
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [baseCombinedProducts]);

    const combinedProducts = useMemo(
        () =>
            baseCombinedProducts.map((product) => ({
                ...product,
                included: includedOverrides[product.id] ?? product.included,
            })),
        [baseCombinedProducts, includedOverrides],
    );

    const displayMonthly = useMemo(() => {
        if (!selectedPlan) {
            return Number(
                combinedProducts
                    .filter((product) => product.included)
                    .reduce((sum, product) => sum + product.monthly, 0)
                    .toFixed(2),
            );
        }
        return selectedPlanDisplayMonthly(selectedPlan, combinedProducts);
    }, [selectedPlan, combinedProducts]);

    const hasActiveSelection = !!selectedPlan;

    const priceIsCash = useMemo(
        () => selectedPlanPriceIsCash(selectedPlan, combinedProducts),
        [selectedPlan, combinedProducts],
    );

    const selectedPartners = useMemo((): SelectedPartner[] => {
        const included = combinedProducts.filter((product) => product.included);
        if (included.length > 0) {
            const ordered: SelectedPartner[] = [];
            const seen = new Set<string>();
            for (const product of included) {
                const label = partnerLabelForCombined(product.partner) || product.partner;
                const key = label.toLowerCase();
                if (!label || seen.has(key)) continue;
                seen.add(key);
                ordered.push({ partner: product.partner, label });
            }
            return ordered;
        }
        if (selectedPlan) {
            const label = selectedPlan.badgeLabel || selectedPlan.partner;
            return label ? [{ partner: selectedPlan.partner, label }] : [];
        }
        return [];
    }, [combinedProducts, selectedPlan]);

    const coveredProducts = useMemo(() => {
        if (selectedPlan) {
            return selectedPlanCoveredProducts(selectedPlan, combinedProducts);
        }
        const ordered: string[] = [];
        const seen = new Set<string>();
        for (const product of combinedProducts.filter((item) => item.included)) {
            for (const tag of product.productTags) {
                for (const part of String(tag).split('+').map((p) => p.trim()).filter(Boolean)) {
                    const key = part.toLowerCase();
                    if (seen.has(key)) continue;
                    seen.add(key);
                    ordered.push(part);
                }
            }
        }
        return ordered;
    }, [selectedPlan, combinedProducts]);

    const identityProducts = useMemo(() => {
        if (coveredProducts.length > 0) return coveredProducts;
        if (!selectedPlan) return [];
        return String(selectedPlan.product)
            .split('+')
            .map((part) => part.trim())
            .filter(Boolean);
    }, [coveredProducts, selectedPlan]);

    const coveredProductsLabel = useMemo(() => identityProducts.join(' + '), [identityProducts]);

    const visibleInsightTags = useMemo(() => {
        if (!selectedPlan) return [];
        return selectedPlan.insightTags.filter((tag) => {
            const lower = tag.toLowerCase();
            if (lower.includes('from pricing grid')) return false;
            if (!priceIsCash && (lower.includes('one-time') || lower.includes('cash'))) return false;
            if (priceIsCash && lower.includes('per month')) return false;
            return true;
        });
    }, [priceIsCash, selectedPlan]);

    /**
     * Only the solar / battery payment is compared with the electric bill. Roof financing is a home
     * improvement, so it stays out of savings even though it is part of the monthly total.
     */
    const paymentSplit = useMemo(() => {
        const included = combinedProducts.filter((product) => product.included);
        if (included.length > 0) return splitAddonMonthlyByBillOffset(included);
        if (!selectedPlan) return { energy: 0, nonEnergy: 0 };
        return { energy: selectedPlan.savingsBasisMonthly, nonEnergy: selectedPlan.nonEnergyMonthly };
    }, [combinedProducts, selectedPlan]);

    const billToday = selectedPlan?.billToday ?? plans[0]?.billToday ?? null;

    const adjustedSavings = useMemo(() => {
        if (priceIsCash && selectedPlan) {
            return {
                monthlySavings: selectedPlan.monthlySavings,
                year1Savings: selectedPlan.year1Savings,
                savings25yr: selectedPlan.savings25yr,
                cumulativeThisPlan: selectedPlan.cumulativeThisPlan,
            };
        }
        if (paymentSplit.energy <= 0) return savingsForMonthlyPayment(null, 0);
        return savingsForMonthlyPayment(billToday, paymentSplit.energy);
    }, [billToday, paymentSplit, priceIsCash, selectedPlan]);

    const billAfterPlan = useMemo(() => {
        if (billToday == null) return null;
        return adjustedSavings.monthlySavings != null
            ? Math.max(0, billToday - adjustedSavings.monthlySavings)
            : billToday;
    }, [adjustedSavings, billToday]);

    const showSavingsStatGrid =
        adjustedSavings.monthlySavings != null && adjustedSavings.monthlySavings > 0;

    const includedProductCount = combinedProducts.filter((p) => p.included).length;

    const combinedQuoteSyncScope = useMemo((): GenerateQuoteSyncScope => {
        const included = combinedProducts.filter((product) => product.included);
        if (included.length > 0) {
            return {
                quotePartners: [...new Set(included.map((product) => product.partner))],
                quoteProductTypes: [
                    ...new Set(
                        included.flatMap((product) =>
                            product.productTags.flatMap((tag) =>
                                atomicProductTypesFromFinancingProduct(tag),
                            ),
                        ),
                    ),
                ],
            };
        }
        if (selectedPlan) {
            return {
                quotePartner: selectedPlan.partner,
                quoteProductTypes: atomicProductTypesFromFinancingProduct(selectedPlan.product),
            };
        }
        return {};
    }, [combinedProducts, selectedPlan]);

    const handleSelectPlan = useCallback(
        (plan: FinancingAiPlan) => {
            setSelectedPlanId(plan.id);
            onSelectPlan?.(plan);
        },
        [onSelectPlan],
    );

    const toggleCombinedProduct = useCallback(
        (id: string) => {
            setIncludedOverrides((prev) => {
                const current =
                    prev[id] ?? baseCombinedProducts.find((product) => product.id === id)?.included ?? true;
                return { ...prev, [id]: !current };
            });
        },
        [baseCombinedProducts],
    );

    const includedLineIds = useMemo(
        () => combinedProducts.filter((product) => product.included).map((product) => product.id),
        [combinedProducts],
    );

    const quoteContext = useMemo(
        (): FinancingAiQuoteContext => ({
            includedLineIds,
            coveredProducts: selectedPlan
                ? selectedPlanCoveredProducts(selectedPlan, combinedProducts)
                : [],
            quoteScope: includedLineIds.length > 0 ? 'combined' : 'plan',
        }),
        [combinedProducts, includedLineIds, selectedPlan],
    );

    const handleGenerateQuote = useCallback(() => {
        if (!selectedPlan) return;
        onGenerateQuote?.(selectedPlan, quoteContext);
    }, [onGenerateQuote, quoteContext, selectedPlan]);

    const handleAltGenerateQuote = useCallback(
        (plan: FinancingAiPlan) => {
            if (generateQuoteDisabled) return;
            const designBlockedReason = getQuoteDesignBlockedReasonForPlan?.(plan);
            if (designBlockedReason) {
                onQuoteDesignBlocked?.(designBlockedReason);
                return;
            }
            handleSelectPlan(plan);
            const ownProducts = String(plan.product)
                .split('+')
                .map((part) => part.trim())
                .filter(Boolean);
            onGenerateQuote?.(plan, {
                includedLineIds: [],
                coveredProducts: ownProducts,
                quoteScope: 'plan',
            });
        },
        [
            generateQuoteDisabled,
            getQuoteDesignBlockedReasonForPlan,
            onQuoteDesignBlocked,
            handleSelectPlan,
            onGenerateQuote,
        ],
    );

    if (plans.length === 0) {
        return (
            <div className="obd-financing-ai obd-financing-ai--empty">
                <p>
                    {loading
                        ? 'Loading pricing from the financing grid…'
                        : 'No pricing grid data yet. Click $ on a design or refresh pricing to load AI-ranked plans.'}
                </p>
            </div>
        );
    }

    return (
        <div className="obd-financing-ai">
            <header className="obd-financing-ai__intro">
                <div>
                    <h3 className="obd-financing-ai__title">Financing options</h3>
                </div>
                <button
                    type="button"
                    className="obd-financing-ai__refresh-btn"
                    onClick={onRefreshAi}
                    disabled={loading}
                >
                    <ArrowsClockwise size={14} weight="bold" aria-hidden />
                    Refresh AI
                </button>
            </header>

            <section
                className={`obd-financing-ai__selected${hasActiveSelection ? '' : ' obd-financing-ai__selected--unselected'}`}
                aria-label={hasActiveSelection ? 'Selected financing plan' : 'No financing plan selected'}
            >
                <SelectedPlanHero
                    hasSelection={hasActiveSelection}
                    partners={selectedPartners}
                    products={hasActiveSelection ? identityProducts : []}
                    insightTags={visibleInsightTags}
                    score={selectedPlan?.aiScore}
                    priceIsCash={priceIsCash}
                    displayMonthly={displayMonthly}
                    selectedPlan={selectedPlan ?? null}
                    generateQuoteDisabled={generateQuoteDisabled}
                    generateQuoteDisabledReason={generateQuoteDisabledReason}
                    quoteDesignBlockedReason={
                        selectedPlan
                            ? getQuoteDesignBlockedReasonForPlan?.(selectedPlan)
                            : undefined
                    }
                    onQuoteDesignBlocked={onQuoteDesignBlocked}
                    getQuoteSyncBlockedHint={getQuoteSyncBlockedHint}
                    quoteSyncBlockedHintForScope={quoteSyncBlockedHintForScope}
                    combinedQuoteSyncScope={combinedQuoteSyncScope}
                    onGenerateQuote={handleGenerateQuote}
                />

                {showSavingsStatGrid ? (
                    <div className="obd-financing-ai__stat-grid">
                        <div className="obd-financing-ai__stat">
                            <span className="obd-financing-ai__stat-label">Monthly savings</span>
                            <span className="obd-financing-ai__stat-value obd-financing-ai__stat-value--positive">
                                {`${formatMoney(adjustedSavings.monthlySavings ?? 0)}/mo`}
                            </span>
                        </div>
                        <div className="obd-financing-ai__stat">
                            <span className="obd-financing-ai__stat-label">Year-1 savings</span>
                            <span className="obd-financing-ai__stat-value obd-financing-ai__stat-value--positive">
                                {formatMoney(adjustedSavings.year1Savings ?? 0, 0)}
                            </span>
                        </div>
                        <div className="obd-financing-ai__stat">
                            <span className="obd-financing-ai__stat-label">25-yr save</span>
                            <span className="obd-financing-ai__stat-value obd-financing-ai__stat-value--positive">
                                {formatMoney(adjustedSavings.savings25yr ?? 0, 0)}
                            </span>
                        </div>
                    </div>
                ) : null}

                <SavingsBasisNote
                    billToday={billToday}
                    energyMonthly={paymentSplit.energy}
                    nonEnergyMonthly={paymentSplit.nonEnergy}
                    isCash={priceIsCash}
                    hasSavings={adjustedSavings.monthlySavings != null}
                />

                <div className="obd-financing-ai__status">
                    <span className="obd-financing-ai__status-pill">
                        <span className="obd-financing-ai__status-dot" aria-hidden />
                        {hasActiveSelection
                            ? `Selected · ${selectedPlan?.aiScore ?? 0}/100${coveredProductsLabel ? ` · ${coveredProductsLabel}` : ''}`
                            : 'Not selected · choose a plan below'}
                    </span>
                </div>

                <div className="obd-financing-ai__selected-body">
                    <div className="obd-financing-ai__selected-charts">
                        {selectedPlan ? (
                            <>
                                <MonthlyCostChart
                                    plan={selectedPlan}
                                    displayMonthly={displayMonthly}
                                    billAfter={billAfterPlan}
                                />
                                <CumulativeSpendChart
                                    plan={selectedPlan}
                                    displayMonthly={displayMonthly}
                                    priceIsCash={priceIsCash}
                                />
                            </>
                        ) : (
                            <div className="obd-financing-ai__chart obd-financing-ai__chart--area">
                                <p className="obd-financing-ai__chart-title">Plan comparison</p>
                                <p className="obd-financing-ai__chart-empty">
                                    Select a plan below to compare monthly cost and 25-year spend.
                                </p>
                            </div>
                        )}
                    </div>

                    <aside className="obd-financing-ai__addons" aria-label="Combined pricing products">
                        <div className="obd-financing-ai__addons-head">
                            <h4 className="obd-financing-ai__addons-title">
                                {hasActiveSelection ? 'In this payment' : 'Available products'}
                            </h4>
                            <span className="obd-financing-ai__addons-count">{includedProductCount}</span>
                        </div>
                        {combinedProducts.length === 0 ? (
                            <p className="obd-financing-ai__addons-empty">
                                No recommended combined products in the pricing grid yet.
                            </p>
                        ) : (
                            <ul className="obd-financing-ai__addons-list" role="list">
                                {combinedProducts.map((product) => (
                                    <li
                                        key={product.id}
                                        className={`obd-financing-ai__addon${
                                            product.included ? '' : ' obd-financing-ai__addon--excluded'
                                        }`}
                                        role="listitem"
                                    >
                                        <span className="obd-financing-ai__addon-thumb" aria-hidden>
                                            {addonProductIcon(product)}
                                        </span>
                                        <div className="obd-financing-ai__addon-copy">
                                            <span className="obd-financing-ai__addon-title">{product.title}</span>
                                            <span className="obd-financing-ai__addon-sub">{product.subtitle}</span>
                                        </div>
                                        <span className="obd-financing-ai__addon-price">
                                            {formatFinancingAiProductPrice(product)}
                                        </span>
                                        <button
                                            type="button"
                                            className={`obd-financing-ai__addon-btn${
                                                product.included
                                                    ? ' obd-financing-ai__addon-btn--remove'
                                                    : ' obd-financing-ai__addon-btn--add'
                                            }`}
                                            aria-label={
                                                product.included
                                                    ? `Remove ${product.title} from this payment`
                                                    : `Add ${product.title} to this payment`
                                            }
                                            title={product.included ? 'Remove from payment' : 'Add to payment'}
                                            onClick={() => toggleCombinedProduct(product.id)}
                                        >
                                            {product.included ? (
                                                <X size={12} weight="bold" aria-hidden />
                                            ) : (
                                                <Plus size={12} weight="bold" aria-hidden />
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </aside>
                </div>
            </section>

            <section className="obd-financing-ai__alternatives" aria-label="Alternative financing plans">
                <div className="obd-financing-ai__alternatives-head">
                    <h4 className="obd-financing-ai__alternatives-title">Alternative plans</h4>
                    <span className="obd-financing-ai__alternatives-count">{plans.length} plans</span>
                </div>

                <div className="obd-financing-ai__alt-grid">
                    {plans.map((plan) => {
                        const isSelected = plan.id === selectedPlanId;
                        const bodyMetrics = altCardBodyMetrics(plan);
                        return (
                            <article
                                key={plan.id}
                                className={`obd-financing-ai__alt-card obd-financing-ai__alt-card--${plan.tone}${
                                    isSelected ? ' obd-financing-ai__alt-card--active' : ''
                                }`}
                            >
                                <div className="obd-financing-ai__alt-card-head">
                                    <div className="obd-financing-ai__alt-head-copy">
                                        <span className="obd-financing-ai__kicker">Finance method</span>
                                        <h5 className="obd-financing-ai__alt-title">{plan.title}</h5>
                                    </div>
                                    <AiScoreRing score={plan.aiScore} />
                                </div>

                                <div className="obd-financing-ai__alt-hero">
                                    <span className="obd-financing-ai__alt-price">{altCardHeroAmount(plan)}</span>
                                    <span className="obd-financing-ai__alt-price-suffix">
                                        {plan.isCash ? '' : '/mo'}
                                    </span>
                                </div>
                                <p className="obd-financing-ai__alt-sub">{plan.subtitle}</p>

                                <div className="obd-financing-ai__alt-metrics">
                                    {bodyMetrics.map((metric) => (
                                        <div key={metric.label} className="obd-financing-ai__alt-metric">
                                            <span className="obd-financing-ai__alt-metric-label">{metric.label}</span>
                                            <span
                                                className={`obd-financing-ai__alt-metric-value${
                                                    metric.tone === 'positive'
                                                        ? ' obd-financing-ai__alt-metric-value--positive'
                                                        : ''
                                                }`}
                                            >
                                                {metric.value}
                                            </span>
                                        </div>
                                    ))}
                                    {plan.escalatorPct != null && !plan.isCash ? (
                                        <div className="obd-financing-ai__alt-metric">
                                            <span className="obd-financing-ai__alt-metric-label">Escalator</span>
                                            <span className="obd-financing-ai__alt-metric-value">
                                                {plan.escalatorPct}%
                                            </span>
                                        </div>
                                    ) : null}
                                </div>

                                {plan.billToday != null && plan.year1Savings == null ? (
                                    <p className="obd-financing-ai__alt-note">
                                        Roof-only financing doesn’t lower the electric bill, so no
                                        utility savings are shown.
                                    </p>
                                ) : null}

                                <div className="obd-financing-ai__status">
                                    <span className="obd-financing-ai__status-pill">
                                        <span className="obd-financing-ai__status-dot" aria-hidden />
                                        {plan.badgeLabel}
                                        {plan.aiScore > 0 ? ` · AI ${plan.aiScore}/100` : ''}
                                    </span>
                                </div>

                                <div className="obd-financing-ai__alt-actions">
                                    <button
                                        type="button"
                                        className={`obd-financing-ai__alt-select${getQuoteDesignBlockedReasonForPlan?.(plan) ? ' obd-financing-ai__alt-select--quote-disabled' : ''}`}
                                        disabled={generateQuoteDisabled}
                                        title={
                                            generateQuoteDisabled
                                                ? generateQuoteDisabledReason
                                                : getQuoteDesignBlockedReasonForPlan?.(plan) ??
                                                  getQuoteSyncBlockedHint?.(plan) ??
                                                  'Open generate quote with this plan'
                                        }
                                        onClick={() => handleAltGenerateQuote(plan)}
                                    >
                                        Generate Quote
                                    </button>
                                    {isSelected ? (
                                        <span
                                            className="obd-financing-ai__alt-check obd-financing-ai__alt-check--active"
                                            title="Selected plan"
                                            role="img"
                                            aria-label="Selected plan"
                                        >
                                            <SealCheck size={18} weight="duotone" />
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            className="obd-financing-ai__alt-check"
                                            title="Select this plan"
                                            onClick={() => handleSelectPlan(plan)}
                                        >
                                            <SealCheck size={18} weight="duotone" />
                                        </button>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default FinancingAiPlansView;
